import React, { useState } from 'react';
import './included.css';
import { FaUsers, FaUserTie, FaFolderOpen, FaRegClock, FaTasks, FaFileInvoiceDollar, FaMoneyCheckAlt, FaRegCalendarCheck, FaTicketAlt, FaCalendarAlt, FaBullhorn, FaUmbrellaBeach, FaChartLine, FaBoxOpen, FaFileContract, FaChartBar, FaShoppingCart, FaBook, FaUniversity, FaComments, FaBriefcase, FaLink, FaEnvelope, FaFileAlt, FaRegSmile, FaQrcode, FaUserPlus, FaVideo, FaBell, FaSms, FaFingerprint, FaSearch, FaServer } from 'react-icons/fa';

// Move this function above the features array and use standard function syntax
function definePriority(label) {
  const top = [
    'Manage Clients', 'Manage Projects', 'Manage Tasks', 'Manage Employees', 'Track Attendance',
    'Send Invoices', 'Record Payments', 'Log Time', 'View Reports', 'Create Estimates'
  ];
  const medium = [
    'Sign Contracts', 'Manage Leaves', 'Performance Reviews', 'Payroll Management',
    'Track Expenses', 'Manage Orders', 'Track Leads', 'Schedule Events', 'Send Messages',
    'Post Notices', 'Knowledge Base', 'Server Management'
  ];

  if (top.includes(label)) return 'top';
  if (medium.includes(label)) return 'medium';
  return 'low';
}

const features = [
  {
    label: 'Manage Clients',
    icon: <FaUsers />,
    description: 'Easily organize, track, and engage with all your clients in one place.',
    category: 'CRM',
    tags: ['clients', 'crm', 'organization']
  },
  {
    label: 'Manage Employees',
    icon: <FaUserTie />,
    description: 'Oversee employee records, roles, and performance with ease.',
    category: 'HR',
    tags: ['employees', 'records', 'performance']
  },
  {
    label: 'Manage Projects',
    icon: <FaFolderOpen />,
    description: 'Plan, monitor, and deliver projects efficiently from start to finish.',
    category: 'Project Management',
    tags: ['projects', 'planning', 'monitoring']
  },
  {
    label: 'Track Attendance',
    icon: <FaRegClock />,
    description: 'Automate attendance tracking and ensure accurate records.',
    category: 'HR',
    tags: ['attendance', 'tracking', 'hr']
  },
  {
    label: 'Manage Tasks',
    icon: <FaTasks />,
    description: 'Assign, prioritize, and track tasks to boost team productivity.',
    category: 'Productivity',
    tags: ['tasks', 'productivity', 'team']
  },
  {
    label: 'Create Estimates',
    icon: <FaFileInvoiceDollar />,
    description: 'Generate professional estimates and quotes for your clients.',
    category: 'Finance',
    tags: ['estimates', 'quotes', 'finance']
  },
  {
    label: 'Send Invoices',
    icon: <FaFileInvoiceDollar />,
    description: 'Create and send invoices to get paid faster and track payments.',
    category: 'Finance',
    tags: ['invoices', 'payments', 'finance']
  },
  {
    label: 'Record Payments',
    icon: <FaMoneyCheckAlt />,
    description: 'Log and manage all incoming payments with detailed records.',
    category: 'Finance',
    tags: ['payments', 'records', 'finance']
  },
  {
    label: 'Log Time',
    icon: <FaRegCalendarCheck />,
    description: 'Track billable hours and project time logs for accurate invoicing.',
    category: 'Productivity',
    tags: ['time tracking', 'billable hours', 'logs']
  },
  {
    label: 'Support Tickets',
    icon: <FaTicketAlt />,
    description: 'Provide customer support and resolve issues with a built-in ticketing system.',
    category: 'Support',
    tags: ['support', 'tickets', 'customer service']
  },
  {
    label: 'Schedule Events',
    icon: <FaCalendarAlt />,
    description: 'Organize meetings, deadlines, and company events in one calendar.',
    category: 'Productivity',
    tags: ['events', 'calendar', 'meetings']
  },
  {
    label: 'Post Notices',
    icon: <FaBullhorn />,
    description: 'Share important announcements and updates with your team.',
    category: 'Communication',
    tags: ['notices', 'announcements', 'updates']
  },
  {
    label: 'Manage Leaves',
    icon: <FaUmbrellaBeach />,
    description: 'Streamline leave requests and approvals for your employees.',
    category: 'HR',
    tags: ['leaves', 'requests', 'approvals']
  },
  {
    label: 'Track Leads',
    icon: <FaChartLine />,
    description: 'Capture and nurture sales leads to grow your business.',
    category: 'CRM',
    tags: ['leads', 'sales', 'crm']
  },
  {
    label: 'Set Holidays',
    icon: <FaUmbrellaBeach />,
    description: 'Define company holidays and automate scheduling.',
    category: 'HR',
    tags: ['holidays', 'scheduling', 'hr']
  },
  {
    label: 'Manage Products',
    icon: <FaBoxOpen />,
    description: 'Organize your product catalog and manage inventory.',
    category: 'Inventory',
    tags: ['products', 'inventory', 'catalog']
  },
  {
    label: 'Track Expenses',
    icon: <FaMoneyCheckAlt />,
    description: 'Monitor and control business expenses with detailed reports.',
    category: 'Finance',
    tags: ['expenses', 'reports', 'finance']
  },
  {
    label: 'Sign Contracts',
    icon: <FaFileContract />,
    description: 'Digitally manage and sign contracts securely.',
    category: 'Operations',
    tags: ['contracts', 'signing', 'digital']
  },
  {
    label: 'View Reports',
    icon: <FaChartBar />,
    description: 'Gain insights with comprehensive business analytics and reports.',
    category: 'Analytics',
    tags: ['reports', 'analytics', 'insights']
  },
  {
    label: 'Manage Orders',
    icon: <FaShoppingCart />,
    description: 'Track and fulfill customer orders efficiently.',
    category: 'Sales',
    tags: ['orders', 'sales', 'tracking']
  },
  {
    label: 'Knowledge Base',
    icon: <FaBook />,
    description: 'Centralize company knowledge and resources for easy access.',
    category: 'Support',
    tags: ['knowledge', 'resources', 'support']
  },
  {
    label: 'Bank Accounts',
    icon: <FaUniversity />,
    description: 'Manage multiple bank accounts and transactions securely.',
    category: 'Finance',
    tags: ['bank', 'accounts', 'transactions']
  },
  {
    label: 'Send Messages',
    icon: <FaComments />,
    description: 'Communicate instantly with your team and clients.',
    category: 'Communication',
    tags: ['messages', 'communication', 'team']
  },
  {
    label: 'Manage Assets',
    icon: <FaBriefcase />,
    description: 'Track company assets and equipment lifecycle.',
    category: 'Operations',
    tags: ['assets', 'equipment', 'lifecycle']
  },
  {
    label: 'Create Biolinks',
    icon: <FaLink />,
    description: 'Generate smart biolinks for your business and team.',
    category: 'Marketing',
    tags: ['biolinks', 'marketing', 'links']
  },
  {
    label: 'Write Letters',
    icon: <FaEnvelope />,
    description: 'Draft and send official letters directly from the platform.',
    category: 'Communication',
    tags: ['letters', 'official', 'communication']
  },
  {
    label: 'Payroll Management',
    icon: <FaFileAlt />,
    description: 'Automate payroll calculations and salary disbursements.',
    category: 'HR',
    tags: ['payroll', 'salary', 'hr']
  },
  {
    label: 'Performance Reviews',
    icon: <FaRegSmile />,
    description: 'Evaluate employee performance and provide feedback.',
    category: 'HR',
    tags: ['performance', 'reviews', 'feedback']
  },
  {
    label: 'Purchase Management',
    icon: <FaShoppingCart />,
    description: 'Streamline purchase requests and approvals.',
    category: 'Procurement',
    tags: ['purchase', 'requests', 'approvals']
  },
  {
    label: 'Generate QR Codes',
    icon: <FaQrcode />,
    description: 'Create QR codes for products, events, and more.',
    category: 'Integration',
    tags: ['qr codes', 'integration', 'products']
  },
  {
    label: 'Recruitment',
    icon: <FaUserPlus />,
    description: 'Manage job postings and candidate applications.',
    category: 'HR',
    tags: ['recruitment', 'jobs', 'applications']
  },
  {
    label: 'Zoom Integration',
    icon: <FaVideo />,
    description: 'Seamlessly schedule and join Zoom meetings.',
    category: 'Integration',
    tags: ['zoom', 'meetings', 'integration']
  },
  {
    label: 'Webhooks',
    icon: <FaBell />,
    description: 'Automate workflows with webhook integrations.',
    category: 'Integration',
    tags: ['webhooks', 'automation', 'integration']
  },
  {
    label: 'SMS Notifications',
    icon: <FaSms />,
    description: 'Send instant SMS alerts and notifications.',
    category: 'Communication',
    tags: ['sms', 'notifications', 'alerts']
  },
  {
    label: 'Biometric Attendance',
    icon: <FaFingerprint />,
    description: 'Enable secure, biometric-based attendance tracking.',
    category: 'HR',
    tags: ['biometric', 'attendance', 'security']
  },
  {
    label: 'Server Management',
    icon: <FaServer />,
    description: 'Monitor, configure, and maintain server infrastructure with comprehensive management tools.',
    category: 'Operations',
    tags: ['servers', 'infrastructure', 'monitoring']
  },
];

const featuresWithPriority = features.map(f => ({ ...f, priority: definePriority(f.label) }));

const grouped = {
  top: featuresWithPriority.filter(f => f.priority === 'top'),
  medium: featuresWithPriority.filter(f => f.priority === 'medium'),
  low: featuresWithPriority.filter(f => f.priority === 'low'),
};

const priorityHeadings = {
  top: { text: 'Core Features', color: '#1e40af', subtitle: 'Essential tools for daily operations' },
  medium: { text: 'Functional & Operational Features', color: '#7c3aed', subtitle: 'Advanced capabilities for growth' },
  low: { text: 'Supportive & Niche Features', color: '#059669', subtitle: 'Specialized tools and integrations' },
};

const IncludedFeatures = () => {
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');

  // Get all unique categories
  const allCategories = ['All', ...Array.from(new Set(features.map(f => f.category)))];

  // Filter features by search term and category
  const filterFeatures = (featuresArr) =>
    featuresArr.filter(f =>
      (selectedCategory === 'All' || f.category === selectedCategory) &&
      (f.label.toLowerCase().includes(search.toLowerCase()) ||
        f.description.toLowerCase().includes(search.toLowerCase()))
    );

  return (
    <div className="included-hero-section">
      <div className="included-hero-content">
        <h1 className="included-hero-title">Comprehensive Business Management Suite</h1>
        <p className="included-hero-pitch">Streamline operations, boost productivity, and drive growth with our integrated platform designed for modern businesses.</p>
      </div>
      <div className="included-features-container">
        <div className="features-header-section">
          <div className="features-header-content">
            <h2 className="included-features-title">Explore Our Features</h2>
            <p className="features-subtitle">Discover powerful tools to transform your business operations</p>
          </div>
          <div className="features-controls">
            <div className="features-search-bar-wrapper">
              <FaSearch className="features-search-icon" />
              <input
                className="features-search-bar"
                type="text"
                placeholder="Search features..."
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>
            <select
              className="features-category-dropdown"
              value={selectedCategory}
              onChange={e => setSelectedCategory(e.target.value)}
            >
              {allCategories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>
        </div>
        {['top', 'medium', 'low'].map(priority => (
          filterFeatures(grouped[priority]).length > 0 ? (
            <div key={priority} className="priority-section">
              <div className="priority-header">
                <h3 className={`priority-heading ${priority}`}>{priorityHeadings[priority].text}</h3>
                <p className="priority-subtitle">{priorityHeadings[priority].subtitle}</p>
              </div>
              <div className="features-grid">
                {filterFeatures(grouped[priority]).map((feature, idx) => (
                  <div className="feature-card" key={feature.label}>
                    <div className="feature-header-row">
                      <div className="feature-icon">{feature.icon}</div>
                      <h4 className="feature-label">{feature.label}</h4>
                    </div>
                    <p className="feature-desc">{feature.description}</p>
                    <div className="feature-tags-row">
                      {feature.tags && feature.tags.map(tag => (
                        <span className="feature-tag" key={tag}>{tag}</span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : null
        ))}
        {/* Empty state handling */}
        {['top', 'medium', 'low'].every(priority => filterFeatures(grouped[priority]).length === 0) && (
          <div className="empty-state">
            <div className="empty-state-title">No features found</div>
            <div className="empty-state-message">
              Try adjusting your search or filter criteria to find what you're looking for.
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default IncludedFeatures;
